import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import {
  Crown,
  Plus,
  Pencil,
  Trash2,
  Upload,
  Loader2,
  Play,
  Image as ImageIcon,
  Save,
  X,
  Film,
  Sparkles,
  Zap,
  Mountain,
  Building2,
  Car
} from "lucide-react";
import { toast } from "sonner";

interface ProfessionalTemplate {
  id: string;
  title: string;
  title_th: string | null;
  description: string | null;
  description_th: string | null;
  video_url: string | null;
  cover_image_url: string | null;
  credits_required: number;
  prompt: string;
  color_class: string;
  icon_name: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const ICON_OPTIONS = [
  { value: 'film', label: 'Film', icon: Film },
  { value: 'crown', label: 'Crown', icon: Crown },
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'zap', label: 'Zap', icon: Zap },
  { value: 'mountain', label: 'Mountain', icon: Mountain },
  { value: 'building', label: 'Building', icon: Building2 },
  { value: 'car', label: 'Car', icon: Car },
];

const COLOR_OPTIONS = [
  { value: 'from-blue-500/20 to-purple-500/20', label: 'Blue to Purple', preview: 'bg-gradient-to-r from-blue-500 to-purple-500' },
  { value: 'from-amber-500/20 to-orange-500/20', label: 'Amber to Orange', preview: 'bg-gradient-to-r from-amber-500 to-orange-500' },
  { value: 'from-pink-500/20 to-rose-500/20', label: 'Pink to Rose', preview: 'bg-gradient-to-r from-pink-500 to-rose-500' },
  { value: 'from-red-500/20 to-orange-500/20', label: 'Red to Orange', preview: 'bg-gradient-to-r from-red-500 to-orange-500' },
  { value: 'from-green-500/20 to-teal-500/20', label: 'Green to Teal', preview: 'bg-gradient-to-r from-green-500 to-teal-500' },
  { value: 'from-slate-500/20 to-gray-500/20', label: 'Slate to Gray', preview: 'bg-gradient-to-r from-slate-500 to-gray-500' },
];

const ProfessionalManagement = () => {
  const [templates, setTemplates] = useState<ProfessionalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    title_th: "",
    description: "",
    description_th: "",
    video_url: "",
    cover_image_url: "",
    credits_required: 70,
    prompt: "",
    color_class: "from-blue-500/20 to-purple-500/20",
    icon_name: "film",
    sort_order: 0,
    is_active: true,
  });

  // Dialog states
  const [showForm, setShowForm] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState("");

  // Fetch templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("professional_templates")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Reset form
  const resetForm = () => {
    setFormData({
      title: "",
      title_th: "",
      description: "",
      description_th: "",
      video_url: "",
      cover_image_url: "",
      credits_required: 70,
      prompt: "",
      color_class: "from-blue-500/20 to-purple-500/20",
      icon_name: "film",
      sort_order: templates.length,
      is_active: true,
    });
    setEditingId(null);
  };

  // Open form for new template
  const handleNew = () => {
    resetForm();
    setShowForm(true);
  };

  // Open form for editing
  const handleEdit = (template: ProfessionalTemplate) => {
    setFormData({
      title: template.title,
      title_th: template.title_th || "",
      description: template.description || "",
      description_th: template.description_th || "",
      video_url: template.video_url || "",
      cover_image_url: template.cover_image_url || "",
      credits_required: template.credits_required,
      prompt: template.prompt,
      color_class: template.color_class,
      icon_name: template.icon_name,
      sort_order: template.sort_order,
      is_active: template.is_active,
    });
    setEditingId(template.id);
    setShowForm(true);
  };

  // Save template
  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.prompt.trim()) {
      toast.error("Prompt is required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Update existing
        const { error } = await supabase
          .from("professional_templates")
          .update(formData)
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Template updated successfully");
      } else {
        // Create new
        const { error } = await supabase
          .from("professional_templates")
          .insert(formData);

        if (error) throw error;
        toast.success("Template created successfully");
      }

      setShowForm(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("professional_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Template deleted successfully");
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  // Toggle active status
  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("professional_templates")
        .update({ is_active: !isActive })
        .eq("id", id);

      if (error) throw error;
      toast.success("Template status updated");
      fetchTemplates();
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Failed to update template");
    }
  };

  // Preview video
  const handlePreviewVideo = (url: string) => {
    setPreviewVideoUrl(url);
    setShowVideoPreview(true);
  };

  const getIconComponent = (iconName: string) => {
    const iconOption = ICON_OPTIONS.find(opt => opt.value === iconName);
    if (!iconOption) return Film;
    return iconOption.icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-500" />
            Professional Mode Templates
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage video templates shown to users in Professional Mode
          </p>
        </div>
        <Button onClick={handleNew} className="gap-2">
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {/* Templates List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => {
          const IconComponent = getIconComponent(template.icon_name);
          return (
            <Card
              key={template.id}
              className={`overflow-hidden ${!template.is_active ? "opacity-60" : ""}`}
            >
              {/* Cover Image / Preview */}
              <div
                className={`relative w-full aspect-video bg-gradient-to-br ${template.color_class} cursor-pointer overflow-hidden`}
                onClick={() => template.video_url && handlePreviewVideo(template.video_url)}
              >
                {template.cover_image_url ? (
                  <img
                    src={template.cover_image_url}
                    alt={template.title}
                    className="w-full h-full object-cover"
                  />
                ) : template.video_url ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Play className="h-8 w-8 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <IconComponent className="h-12 w-12 text-white/50" />
                  </div>
                )}
                {!template.is_active && (
                  <Badge className="absolute top-2 right-2" variant="secondary">
                    Inactive
                  </Badge>
                )}
                {template.video_url && !template.cover_image_url && (
                  <Badge className="absolute top-2 left-2" variant="outline">
                    Click to preview
                  </Badge>
                )}
              </div>

              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <IconComponent className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{template.title}</CardTitle>
                  </div>
                  <Switch
                    checked={template.is_active}
                    onCheckedChange={() => handleToggleActive(template.id, template.is_active)}
                  />
                </div>
                {template.title_th && (
                  <p className="text-sm text-muted-foreground">{template.title_th}</p>
                )}
              </CardHeader>

              <CardContent className="space-y-3">
                {template.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {template.description}
                  </p>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Credits:</span>
                  <Badge variant="outline">{template.credits_required}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(template)}
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Template?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete the template.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(template.id)}
                          className="bg-destructive text-destructive-foreground"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Template" : "New Template"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update the professional mode template details"
                : "Create a new professional mode template"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title (English) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Cinematic Film"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title_th">Title (Thai)</Label>
                <Input
                  id="title_th"
                  value={formData.title_th}
                  onChange={(e) => setFormData({ ...formData, title_th: e.target.value })}
                  placeholder="ภาพยนตร์ระดับฮอลลีวูด"
                />
              </div>
            </div>

            {/* Description */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="description">Description (English)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Professional cinematic quality with dramatic lighting"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description_th">Description (Thai)</Label>
                <Textarea
                  id="description_th"
                  value={formData.description_th}
                  onChange={(e) => setFormData({ ...formData, description_th: e.target.value })}
                  placeholder="คุณภาพระดับภาพยนตร์พร้อมแสงไฟที่ตัดขาด"
                  rows={2}
                />
              </div>
            </div>

            {/* Video URL */}
            <div className="space-y-2">
              <Label htmlFor="video_url">Video URL (Example Video)</Label>
              <Input
                id="video_url"
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                placeholder="https://example.com/video.mp4"
              />
              <p className="text-xs text-muted-foreground">
                URL to an example video. Users can preview this by clicking the cover image.
              </p>
            </div>

            {/* Cover Image URL */}
            <div className="space-y-2">
              <Label htmlFor="cover_image_url">Cover Image URL</Label>
              <Input
                id="cover_image_url"
                value={formData.cover_image_url}
                onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                placeholder="https://example.com/cover.jpg"
              />
              <p className="text-xs text-muted-foreground">
                URL to a cover image. If not provided, will show a colored placeholder with icon.
              </p>
            </div>

            {/* Credits Required */}
            <div className="space-y-2">
              <Label htmlFor="credits">Credits Required</Label>
              <Input
                id="credits"
                type="number"
                value={formData.credits_required}
                onChange={(e) => setFormData({ ...formData, credits_required: parseInt(e.target.value) || 70 })}
                min={1}
              />
              <p className="text-xs text-muted-foreground">
                Default is 70 credits (2x normal Sora 2 15s)
              </p>
            </div>

            {/* Hidden Prompt (CRITICAL - NOT SHOWN TO USERS) */}
            <div className="space-y-2">
              <Label htmlFor="prompt" className="flex items-center gap-2">
                <span className="text-destructive">*</span>
                Actual AI Prompt
                <Badge variant="destructive" className="text-xs">
                  HIDDEN FROM USERS
                </Badge>
              </Label>
              <Textarea
                id="prompt"
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                placeholder="Create a cinematic masterpiece with professional-grade quality. Use advanced lighting..."
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-destructive font-medium">
                ⚠️ This prompt is NEVER shown to users. It is the actual prompt sent to the AI.
              </p>
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map((option) => {
                  const IconComp = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon_name: option.value })}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        formData.icon_name === option.value
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <IconComp className="h-5 w-5" />
                      <span className="text-sm">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="space-y-2">
              <Label>Color Theme</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color_class: option.value })}
                    className={`relative p-2 rounded-lg border-2 transition-all ${
                      formData.color_class === option.value
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className={`w-16 h-8 rounded ${option.preview}`} />
                    <span className="text-xs block mt-1">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Order */}
            <div className="space-y-2">
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                min={0}
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers appear first
              </p>
            </div>

            {/* Active */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div>
                <Label htmlFor="is_active" className="mb-0">Active</Label>
                <p className="text-xs text-muted-foreground">Show this template to users</p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Preview Dialog */}
      <Dialog open={showVideoPreview} onOpenChange={setShowVideoPreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Video Preview</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {previewVideoUrl && (
              <video
                src={previewVideoUrl}
                controls
                className="w-full h-full"
                autoPlay
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfessionalManagement;
